import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { FormEvent, useEffect, useRef, useState } from 'react';

import { useDataFetch } from 'src/hooks/useDataFetch';

import { PageSearchResults } from 'src/types/components';

const prefillInput = (form: HTMLFormElement, searchParams: URLSearchParams, fieldName: string) => {
  const term = searchParams.get('term') ?? '';
  const input = form.querySelector(`[name=${fieldName}]`) as HTMLInputElement;
  if (input) {
    input.value = term;
  }
};

const FORM_FIELDS = [
  {
    name: 'term',
    prefill: prefillInput,
  },
];

export const PAGE_ITEM_COUNT = 10;

export const usePageSearch = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [searchKey, setSearchKey] = useState<string | null>(null);
  const { data, isLoading, error } = useDataFetch<PageSearchResults>(searchKey);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const updateSearchKey = () => {
    // get params from the URL
    const params = new URLSearchParams(window.location.search);

    // find / parse the page #
    let page = 1;
    const potentialPage = parseInt(params?.get('page') ?? '1');
    page = isNaN(potentialPage) ? 1 : potentialPage;

    // transform page # into SKIP amount
    params.set('skip', Math.max(0, (page - 1) * PAGE_ITEM_COUNT).toString());
    params.set('limit', PAGE_ITEM_COUNT.toString());
    params.delete('page');

    // update the form with the URL params
    updateForm(params);

    // filter out empty params
    [...params.entries()]
      .filter(([_key, value]) => value === '')
      .forEach(([key]) => params.delete(key));

    // transform and set the search key
    const querystring = params.toString();
    if (querystring.length === 0) {
      return;
    }
    setSearchKey(`/api/search?${querystring}`);
  };

  const updateForm = (params: URLSearchParams) => {
    const form = formRef.current;
    if (form) {
      FORM_FIELDS.forEach(({ name, prefill }) => {
        prefill(form, params, name);
      });
    }
  };

  const onSubmit = async (event?: FormEvent) => {
    event?.preventDefault();

    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const formFields = new URLSearchParams(formData as unknown as Record<string, string>);

      // with new fields, revert to page 0
      const page = '1';

      // Remove query params that are empty strings
      [...formFields.entries()]
        .filter(([_key, value]) => value === '')
        .forEach(([key]) => formFields.delete(key));

      formFields.set('page', page);

      const querystring = formFields.toString();
      router.push(`${pathname}?${querystring}`);
    }
  };

  const clearField = (field: string, reFocus = false) => {
    updateForm(new URLSearchParams({ [field]: '' }));
    onSubmit();
    if (reFocus && formRef.current) {
      const toFocus = formRef.current.querySelector(`[name=${field}]`) as HTMLInputElement;
      toFocus.focus();
    }
  };

  const adjustPage = (newPage: number) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('page', newPage.toString());

    // update search + url
    const querystring = urlParams.toString();
    // setSearchKey(`/api/search?${querystring}`);
    router.push(`${pathname}?${querystring}`);
  };

  useEffect(() => {
    updateSearchKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return {
    formRef,
    onSubmit,
    clearField,
    adjustPage,
    searchKey: searchKey || '',
    data,
    isLoading,
    error,
  };
};
